<template>
    <div class="override-list">
        <div
            class="override-list__header"
            :data-test-id="`overrides-header-${flag.name}`"
            :data-focus-key="`overrides-header-${flag.name}`"
            role="button"
            tabindex="0"
            :aria-expanded="isExpanded"
            :aria-controls="bodyId"
            :aria-label="headerLabel"
            @click="$emit('toggleExpanded')"
            @keydown.enter.prevent="$emit('toggleExpanded')"
            @keydown.space.prevent="$emit('toggleExpanded')"
        >
            <span
                class="override-list__caret"
                :class="{ 'override-list__caret--expanded': isExpanded }"
                aria-hidden="true"
            />
            <span
                class="override-list__count"
                :class="{ 'override-list__count--warning': hasOverrides }"
                :data-test-id="`overrides-count-${flag.name}`"
            >
                {{ countText }}
            </span>
        </div>

        <div
            v-if="isExpanded"
            :id="bodyId"
            class="override-list__body"
            :data-test-id="`overrides-body-${flag.name}`"
        >
            <p
                v-if="isPending"
                :data-test-id="`overrides-loading-${flag.name}`"
            >
                Loading organization overrides...
            </p>

            <p
                v-else-if="loadError"
                class="section-error"
                :data-test-id="`overrides-error-${flag.name}`"
            >
                {{ loadError }}
            </p>

            <template v-else>
                <div
                    v-for="organization in overridingOrganizations"
                    :key="organization.id"
                    class="override-list__row"
                    :data-test-id="`override-row-${flag.name}-${organization.id}`"
                >
                    <button
                        v-if="canUpdate"
                        type="button"
                        class="override-list__delete icon-delete"
                        title="Delete Override"
                        :aria-label="`Delete Override for ${organization.name}`"
                        :disabled="isBusy || isSaving(organization.id)"
                        :data-test-id="`override-delete-${flag.name}-${organization.id}`"
                        :data-focus-key="`override-delete-${flag.name}-${organization.id}`"
                        @click="$emit('delete', { organizationId: organization.id })"
                    />
                    <span
                        class="override-list__name"
                        :data-test-id="`override-name-${flag.name}-${organization.id}`"
                    >
                        {{ organization.name }}: turned {{ stateOf(organization) }}
                    </span>
                </div>

                <button
                    v-if="canUpdate && !isAdding"
                    type="button"
                    class="override-list__add-toggle"
                    :disabled="isBusy"
                    :data-test-id="`override-add-toggle-${flag.name}`"
                    :data-focus-key="`override-add-toggle-${flag.name}`"
                    @click="isAdding = true"
                >
                    {{ addToggleText }}
                </button>

                <div
                    v-else-if="canUpdate"
                    class="override-list__add"
                    :data-test-id="`override-add-form-${flag.name}`"
                >
                    <label
                        class="visually-hidden"
                        :for="addSelectorId"
                    >
                        Organization to override {{ flagLabel || flag.name }}
                    </label>
                    <organization-selector
                        class="override-list__selector"
                        allow-clear
                        :input-id="addSelectorId"
                        placeholder="Choose an organization"
                        :exclude-ids="overridingOrganizationIds"
                        :organization-id="addOrganizationId"
                        @update="addOrganizationId = $event"
                    />
                    <button
                        type="button"
                        class="btn btn-primary"
                        :disabled="isBusy || addOrganizationId === null || isSaving(addOrganizationId)"
                        :data-test-id="`override-add-confirm-${flag.name}`"
                        @click="confirmAdd"
                    >
                        Add
                    </button>
                    <button
                        type="button"
                        class="btn"
                        :data-test-id="`override-add-cancel-${flag.name}`"
                        @click="cancelAdd"
                    >
                        Cancel
                    </button>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import OrganizationSelector from '@/components/OrganizationSelector.vue'
import selectOverriding from './overrides'

export default {
    name: 'OrganizationOverrideList',

    components: { OrganizationSelector },

    props: {
        flag: {
            type: Object,
            required: true,
        },
        flagLabel: {
            type: String,
            default: '',
        },
        globalValue: {
            type: Boolean,
            default: false,
        },
        organizations: {
            type: Array,
            default: () => [],
        },
        isExpanded: {
            type: Boolean,
            default: false,
        },
        // Permission alone: false removes the write controls. Anything
        // transient — an in-flight write, a payload still loading — belongs in
        // isBusy below, which dims them instead. Conflating the two would pull
        // rows out from under the pointer mid-save.
        canUpdate: {
            type: Boolean,
            default: false,
        },
        isBusy: {
            type: Boolean,
            default: false,
        },
        savingOrganizationIds: {
            type: Array,
            default: () => [],
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        hasLoaded: {
            type: Boolean,
            default: false,
        },
        loadError: {
            type: String,
            default: null,
        },
    },

    emits: ['toggleExpanded', 'delete', 'add'],

    data() {
        return {
            isAdding: false,
            addOrganizationId: null,
        }
    },

    computed: {
        overridingOrganizations() {
            return selectOverriding(this.organizations, this.flag.name)
        },

        overridingOrganizationIds() {
            return this.overridingOrganizations.map((organization) => organization.id)
        },

        // Loading is reported only while nothing has ever loaded, so a
        // refetch leaves the rows on screen — disabled by isBusy — instead
        // of blanking them. An empty `organizations` is not a first load: a
        // global write purges every override row.
        isPending() {
            return this.isLoading && !this.hasLoaded
        },

        hasOverrides() {
            if (this.loadError || this.isLoading) {
                return false
            }

            return this.overridingOrganizations.length > 0
        },

        bodyId() {
            return `overrides-body-${this.flag.name}`
        },

        addSelectorId() {
            return `override-add-organization-${this.flag.name}`
        },

        // True only once the chosen organization's row actually exists and its
        // write has finished. The page applies the override optimistically, so
        // membership alone would close the form mid-request and leave a failed
        // add with nothing selected; a failure reverts membership before the
        // save clears, so this never flips for one.
        addHasLanded() {
            if (this.addOrganizationId === null) {
                return false
            }

            return (
                this.overridingOrganizationIds.includes(this.addOrganizationId) &&
                !this.isSaving(this.addOrganizationId)
            )
        },

        // Without this the headers all announce as their count alone,
        // giving a screen reader no way to tell one flag's list from another.
        headerLabel() {
            return `${this.flagLabel || this.flag.name} organization overrides: ${this.countText}`
        },

        // Adding an override always lands on the opposite of the current
        // global value — that divergence is what makes the endpoint report
        // the new row as an override.
        // No direction is named while a load is in flight: globalValue still
        // holds the pre-write baseline until the matrix settles.
        addToggleText() {
            if (this.loadError || this.isLoading) {
                return '+ Add Override'
            }

            return `+ Add Override to ${this.globalValue ? 'Off' : 'On'}`
        },

        // Every override the endpoint reports diverges from the global, so
        // the value they were turned to is the opposite of it.
        countText() {
            if (this.loadError || this.isLoading) {
                return '—'
            }

            const count = this.overridingOrganizations.length
            const noun = count === 1 ? 'override' : 'overrides'

            if (count === 0) {
                return `${count} ${noun}`
            }

            return `${count} ${noun} to ${this.globalValue ? 'Off' : 'On'}`
        },
    },

    watch: {
        isExpanded(value) {
            if (!value) {
                this.cancelAdd()
            }
        },

        // Losing the permission hides the form; drop its state too, or a
        // regained permission reopens it holding a stale selection.
        canUpdate(value) {
            if (!value) {
                this.cancelAdd()
            }
        },

        // The add is behind a confirmation prompt and then a request, so the
        // form cannot close on click — cancelling the prompt or a failed write
        // would silently discard the selection.
        addHasLanded(landed) {
            if (landed) {
                this.cancelAdd()
            }
        },
    },

    methods: {
        stateOf(organization) {
            return organization.flags[this.flag.name] ? 'On' : 'Off'
        },

        isSaving(organizationId) {
            return this.savingOrganizationIds.includes(organizationId)
        },

        confirmAdd() {
            this.$emit('add', { organizationId: this.addOrganizationId })
        },

        cancelAdd() {
            this.isAdding = false
            this.addOrganizationId = null
        },
    },
}
</script>

<style scoped lang="scss">
.override-list__header {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 8px 0;
    width: fit-content;

    // role="button" on a div, so the app-wide button focus rule in
    // _general.scss does not reach it.
    &:focus-visible {
        outline: 2px solid #fff;
        outline-offset: 2px;
    }
}

.override-list__caret {
    width: 25px;
    height: 25px;
    background-image: url(@images/icon-right-arrow.svg);
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    transition: transform 0.15s ease-in-out;

    &--expanded {
        transform: rotate(90deg);
    }
}

.override-list__count {
    font-family: 'Avenir Book', sans-serif;
    font-size: 12px;
    color: #c9c9c9;

    &--warning {
        color: #ffa8a2;
    }
}

.override-list__body {
    margin: 4px 0 16px 26px;
}

.override-list__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 0;
    font-family: 'Avenir Book', sans-serif;
    font-size: 13px;
    color: #fff;
}

.override-list__name {
    min-width: 220px;
}

.override-list__delete {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: #ffa8a2;
    font-size: 22px;
    line-height: 1;

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
}

.override-list__add-toggle {
    background: none;
    border: none;
    padding: 6px 0;
    cursor: pointer;

    color: #c9c9c9;
    font-family: 'Avenir Book', sans-serif;
    font-size: 13px;

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
}

.override-list__add {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
}

.override-list__selector {
    min-width: 260px;
    max-width: 320px;
}

.section-error {
    color: #ffa8a2;
    font-family: 'Avenir Heavy', sans-serif;
}
</style>
